/**
 * Hook to format currency using company settings.
 * Use everywhere for money display - no hardcoded Rs, $, PKR.
 * currencySymbol = display symbol (e.g. Rs. for PKR, $ for USD, ¥ for CNY).
 * Pass an optional currencyCode to format document FC amounts (e.g. purchase CNY).
 */
import { useCallback, useMemo } from 'react';
import { useSettings } from '@/app/context/SettingsContext';
import { formatCurrency as formatCurrencyUtil, formatCurrencyCompact as formatCurrencyCompactUtil, getCurrencySymbol } from '@/app/utils/formatCurrency';

export function useFormatCurrency() {
  const { company } = useSettings();
  const currency = company?.currency || 'PKR';
  const decimalPrecision = company?.decimalPrecision ?? 2;
  const currencySymbol = useMemo(() => getCurrencySymbol(currency), [currency]);

  const formatCurrency = useCallback(
    (value: number, currencyCode?: string) =>
      formatCurrencyUtil(value, currencyCode || currency, decimalPrecision),
    [currency, decimalPrecision]
  );

  const formatCurrencyCompact = useCallback(
    (value: number, currencyCode?: string) =>
      formatCurrencyCompactUtil(value, currencyCode || currency, decimalPrecision),
    [currency, decimalPrecision]
  );

  return { formatCurrency, formatCurrencyCompact, currency, currencySymbol, decimalPrecision };
}
